import { onUnmounted } from "vue";

/** A participant that can be deactivated when another group member becomes active. */
export type ExclusiveGroupMember = {
  deactivate: () => void;
};

/** Coordinates mutually exclusive activation across registered members. */
export type UseExclusiveGroupReturn = {
  /** Adds a member and unregisters it automatically when the current owner unmounts. */
  register: (member: ExclusiveGroupMember) => void;
  /** Deactivates every registered member except the activating member. */
  deactivateOthers: (activeMember: ExclusiveGroupMember) => void;
  /** Deactivates every registered member. */
  deactivateAll: () => void;
};

/**
 * Coordinates mutually exclusive active-id registries within a Vue owner.
 *
 * Registered members are removed automatically when their owning component or composable
 * unmounts. Call `register` from setup so Vue can associate cleanup with that owner.
 */
export const useExclusiveGroup = (): UseExclusiveGroupReturn => {
  const members = new Set<ExclusiveGroupMember>();

  const register = (member: ExclusiveGroupMember) => {
    members.add(member);
    onUnmounted(() => members.delete(member));
  };

  const deactivateOthers = (activeMember: ExclusiveGroupMember) => {
    members.forEach((member) => {
      if (member !== activeMember) member.deactivate();
    });
  };

  const deactivateAll = () => {
    members.forEach((member) => member.deactivate());
  };

  return { register, deactivateOthers, deactivateAll };
};
